import { useDraggable } from "@dnd-kit/core";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    RiAccountCircleLine,
    RiCalendarLine, RiCheckboxBlankCircleLine, RiCheckboxCircleFill, RiDiscussLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, { BaseHTMLAttributes, useMemo } from "react";
import ReactTimeago from "react-timeago";
import {
    DashboardView, TicketMessageType,
} from "../../../../__generated__/api";
import useRouting, { Link, useParams } from "../../../../contexts/Routing";
import { TicketItem } from "../../../../contexts/ticket-state";
import { useTicketState } from "../../../../contexts/ticket-state/TicketState";
import { tryParseDate } from "../../../../date";
import Avatar from "../../../avatar/Avatar";
import Button from "../../../theme/button/Button";
import Priority from "../../labels/priority/Priority";
import RequestType from "../../labels/request-type/RequestType";
import Queue from "../../labels/queue/Queue";
import Source from "../../labels/source/Source";
import Styles from "./Card.module.scss";
import Sla from "../../labels/sla/Sla";

export enum CardFields {
    SUBJECT = "urn:fq:form-field:subject",
    LAST_REPLY_MESSAGE = "urn:fq:form-field:lastReplyMessage",
    LAST_REPLY_DATE = "urn:fq:form-field:lastReplyDate",
    PRIORITY = "urn:fq:form-field:priority",
    SLA = "urn:fq:form-field:sla",
    TICKET_SOURCE = "urn:fq:form-field:ticketSource",
    ASSIGNEE_ID = "urn:fq:form-field:assigneeId",
    REQUESTER_ID = "urn:fq:form-field:requesterId",
    CREATED_DATE = "urn:fq:form-field:createdDate",
    REQUEST_TYPE = "urn:fq:form-field:requestType",
    QUEUE_ID = "urn:fq:form-field:queueId",
    REPLY_COUNT = "urn:fq:form-field:replyCount",
}

export interface ColumnProps {
    ticketItem: TicketItem;
    isDragging?: boolean,
    isHidden?: boolean
}

export function CardElement(props: {
    nodeType: string
} & BaseHTMLAttributes<HTMLElement>) {
    const {
        nodeType,
        children,
        className,
        ...rest
    } = props;
    if (nodeType === "td") {
        return (
            <td
                className={classNames("card-element", className)}
                {...rest}
            >
                {children}
            </td>
        );
    }
    return (
        <div
            className={classNames("card-element", className)}
            {...rest}
        >
            {children}
        </div>
    );
}

export default function Card({
    ticketItem,
    isDragging,
    isHidden,
}: ColumnProps) {
    const ticketContext = useTicketState();
    const { Paths, createHistoryPush, getSearchParams } = useRouting();
    const { chatId: currentChatId } = useParams(Paths.AGENT_CHAT);
    const ticket = ticketItem.useUpdates();
    const cardRef = ticketItem.useCardRef();

    const view = ticketContext.currentDashboard?.settings?.view ?? DashboardView.Kanban;
    const createdDate = new Date(ticket.createdDate);
    const selectedFields = useMemo(
        () => new Set(ticketContext.currentDashboard?.settings?.fieldIds ?? Object.values(CardFields)),
        [ticketContext.currentDashboard],
    );

    const {
        attributes, listeners, setNodeRef, transform,
    } = useDraggable({
        id: ticket.id,
    });

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    const lastDate = tryParseDate(ticket.lastUserMessageDate ?? ticket.createdDate) ?? new Date();
    const { requester } = ticketItem;
    const cardSelector = (
        <Button
            className="card-selector"
            variant="none"
            size="small"
            prefixIcon={ticketItem.isChecked ? <RiCheckboxCircleFill /> : <RiCheckboxBlankCircleLine />}
            onClick={event => {
                event.stopPropagation();
                event.preventDefault();
                ticketItem.setIsChecked(!ticketItem.isChecked);
            }}
        />
    );
    const nodeType = view === DashboardView.Compact ? "td" : "div";

    const contentTicketNumber = (
        <CardElement nodeType={nodeType} className="card-row-ticket-number">
            #
            {ticket.ticketNumber}
        </CardElement>
    );
    const contentLastReply = selectedFields.has(CardFields.LAST_REPLY_DATE) ? (
        <CardElement nodeType={nodeType} className="card-row-created-date">
            <ReactTimeago
                date={lastDate}
                title={`${ticket.lastUserMessageDate ? "Last message from user" : "Created"
                }: ${lastDate?.toLocaleString()}`}
            />
        </CardElement>
    ) : undefined;

    const contentSubject = selectedFields.has(CardFields.SUBJECT) ? (
        <CardElement nodeType={nodeType} className="card-row-subject" title="Ticket subject">
            {ticket.subject}
        </CardElement>
    ) : undefined;

    const contentLastReplyMessage = selectedFields.has(CardFields.LAST_REPLY_MESSAGE) ? (
        <CardElement nodeType={nodeType} className="card-row-last-message" title="Most recent message from user">
            {ticket.lastMessage}
        </CardElement>
    ) : undefined;

    const contentPriority = selectedFields.has(CardFields.PRIORITY) ? (
        <CardElement nodeType={nodeType} className="card-row-priority">
            <Priority ticketPriority={ticketItem.priority} />
        </CardElement>
    ) : undefined;

    const contentSla = selectedFields.has(CardFields.SLA) ? (
        <CardElement nodeType={nodeType} className="card-row-sla">
            <Sla ticket={ticketItem} />
        </CardElement>
    ) : undefined;

    const contentRequestType = selectedFields.has(CardFields.REQUEST_TYPE) ? (
        <CardElement nodeType={nodeType} className="card-row-request-type">
            <RequestType requestType={ticketItem.ticket?.form?.name} />
        </CardElement>
    ) : undefined;
    const contentQueue = selectedFields.has(CardFields.QUEUE_ID) ? (
        <CardElement nodeType={nodeType} className="card-row-queue">
            <Queue ticket={ticketItem} />
        </CardElement>
    ) : undefined;

    const contentTicketSource = selectedFields.has(CardFields.TICKET_SOURCE) ? (
        <CardElement nodeType={nodeType} className="card-row-source" title={`Conversation Source: ${ticket.source?.text}`}>
            <Source text={ticketItem.chat.source?.text} type={ticketItem.chat.source?.type} />
        </CardElement>
    ) : undefined;

    const contentAssignee = selectedFields.has(CardFields.ASSIGNEE_ID) ? (
        <CardElement nodeType={nodeType} className="card-assignee" title={`Assignee: ${ticket.assignee?.name}` as string}>
            {ticket.assignee ? (
                <>
                    <Avatar src={ticket.assignee?.avatarUrl as string} variant="xs" />
                    {ticket.assignee.name}
                </>
            ) : (
                <>
                    <RiAccountCircleLine className="icon-small" />
                    {" "}
                    Unassigned
                </>
            )}

        </CardElement>
    ) : undefined;

    const contentRequester = selectedFields.has(CardFields.REQUESTER_ID) ? (
        <CardElement
            nodeType={nodeType}
            className="card-requester"
            title={`Requested by ${
                requester?.name
            } from ${
                requester.organization?.name ?? requester.group
            }` as string}
        >
            <Avatar src={requester.avatarUrl as string} variant="xs" />
            {requester.name}

            <span className="card-requester-company">
                {requester.organization?.name ?? requester.group}
            </span>
        </CardElement>
    ) : undefined;

    const contentCreatedDate = selectedFields.has(CardFields.CREATED_DATE) ? (

        <CardElement nodeType={nodeType} className="card-created-date" title={`Created Date: ${createdDate.toLocaleString()}`}>
            <RiCalendarLine className="card-meta-icon" />
            {" "}
            {createdDate.toLocaleDateString().replace(
                createdDate.getFullYear().toString(),
                (createdDate.getFullYear() % 1000).toString(),
            )}
        </CardElement>
    ) : undefined;

    const contentReplyCount = selectedFields.has(CardFields.REPLY_COUNT) ? (
        <CardElement nodeType={nodeType} className="card-message-count" title="Message Count">
            <RiDiscussLine className="card-meta-icon" />
            {" "}
            {ticketItem.ticket.messageCount
                ?? ticketItem.messages?.count(msg => msg.type === TicketMessageType.UserMessage)
                ?? 0}
        </CardElement>
    ) : undefined;

    const chatId = (ticketItem.chat.ticketNumber ?? ticketItem.id).toString();

    if (view === DashboardView.Compact) {
        const onClicked = createHistoryPush(Paths.AGENT_CHAT, { chatId });
        return (
            <tr
                data-tour-panel="card-item"
                ref={cardRef}
                className={classNames(
                    Styles.card,
                    Styles[`card-${view?.toLowerCase()}`],
                    ticketItem.isUnread ? "card-unread" : "card-read",
                    {
                        "card-dragging": isDragging,
                        "card-hidden": isHidden,
                        active: ticketItem.isChecked,
                        [Styles["card-open"]]: chatId === currentChatId,
                    },
                )}
                onClick={event => {
                    if (ticketContext.selectionState.isNotEmpty) {
                        ticketItem.setIsChecked(!ticketItem.isChecked);
                        event.stopPropagation();
                        event.preventDefault();
                    } else {
                        onClicked();
                    }
                }}
            >

                <CardElement nodeType={nodeType}>
                    {cardSelector}
                </CardElement>
                {contentTicketNumber}
                {contentSla}
                {contentSubject}
                {contentLastReplyMessage}
                {contentPriority}
                {contentRequestType}
                {contentQueue}
                {contentTicketSource}

                {contentAssignee}
                {contentCreatedDate}
                {contentReplyCount}
                {contentLastReply}
                {contentRequester}
            </tr>
        );
    }

    return (

        <Link
            data-tour-panel="card-item"
            to={Paths.AGENT_CHAT}
            params={{ chatId }}
            search={getSearchParams(Paths.AGENT)}
            className={classNames(
                Styles.card,
                Styles[`card-${view?.toLowerCase()}`],
                ticketItem.isUnread ? "card-unread" : "card-read",
                {
                    "card-dragging": isDragging,
                    "card-hidden": isHidden,
                    active: ticketItem.isChecked,
                    [Styles["card-open"]]: chatId === currentChatId,
                },
            )}
            onClick={event => {
                if (ticketContext.selectionState.isNotEmpty) {
                    ticketItem.setIsChecked(true);
                    event.stopPropagation();
                    event.preventDefault();
                }
            }}
            style={style}
            ref={ref => {
                setNodeRef(ref);
                cardRef(ref);
            }}
            {...listeners}
            {...attributes}
        >

            {view === DashboardView.Kanban && (
                <>
                    <div className="card-row-pre-data">
                        {contentTicketNumber}
                        {contentLastReply}
                    </div>
                    {contentSubject}
                    {contentLastReplyMessage}
                    <div className="card-row-tags">
                        {contentSla}
                        {contentPriority}
                        {contentRequestType}
                        {contentTicketSource}
                        {contentQueue}
                    </div>
                    {(selectedFields.has(CardFields.ASSIGNEE_ID)
                    || selectedFields.has(CardFields.CREATED_DATE)
                    || selectedFields.has(CardFields.REPLY_COUNT)) && (
                        <div className="card-row-meta">
                            {contentAssignee}
                            {contentCreatedDate}
                            {contentReplyCount}
                            {!selectedFields.has(CardFields.REQUESTER_ID) && cardSelector}
                        </div>
                    )}

                    {selectedFields.has(CardFields.REQUESTER_ID) && (
                        <div className="card-row-meta">
                            {contentRequester}
                            {cardSelector}
                        </div>
                    )}
                </>
            )}
        </Link>
    );
}

Card.defaultProps = {
    isDragging: false,
    isHidden: false,
};
